import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Printer, Radar } from "lucide-react";
import React, { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import * as ozon from "@/api/ozonSupply";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";
import { useUrlParam } from "@/lib/url-state";
import { cn } from "@/lib/utils";
import { fmt, fmtDateTime, int0, plural, timeAgo } from "../format";
import { ChangeModeBar } from "./ChangeModeBar";
import { ChangePreview } from "./ChangePreview";
import { ContentPanel } from "./ContentPanel";
import { KindPicker } from "./KindPicker";
import { PlacesSection } from "./PlacesSection";
import { SlotPicker } from "./SlotPicker";
import { SlotWatchCard } from "./SlotWatchCard";
import { SlotWatchDialog } from "./SlotWatchDialog";
import { StageActions } from "./StageActions";
import { StageProgress } from "./StageProgress";
import { WarehousePicker } from "./WarehousePicker";
import { OnecBanner, StageBadge } from "./ozonBadges";
import { PalletPlanBanner, PalletPlanConflict } from "./PalletPlanBanner";
import { dirKey } from "./ozonQueries";
import {
  actionsFor,
  defaultTab,
  draftStale,
  stageOf,
  type ActionKey,
  type OzonTab,
} from "./ozonStage";
import { applyBlock, editing, reprintBlock } from "./changeModel";
import { takenOffer } from "./dropOff";
import { fmtSlot } from "./slotFormat";
import { WATCH_LOCK, canWatch, lockForWatch } from "./slotWatch";
import { useOzonChange } from "./useOzonChange";
import { useOzonDirectionActions } from "./useOzonDirectionActions";
import { useOzonLayout } from "./useOzonLayout";
import { WATCH_POLL_MS, useNow, useSlotWatch } from "./useSlotWatch";

/** Страница одной поставки Ozon: где она, что делать дальше — и один раздел под этим.
 *
 *  Поставка живёт неделями и проходит шесть шагов, а в списке из двух десятков направлений
 *  раскрытая раскладка занимает весь экран и теряет соседей. Адрес `/supply/ozon/:id?tab=…`
 *  можно прислать коллеге, открыть после перезагрузки и снять харнессом скриншотов.
 *
 *  ⚠️ Кнопка на экране одна — главная для стадии; остальное под «…». Набор считает
 *  `actionsFor`, и это ЕДИНСТВЕННОЕ место, где решается «что сейчас можно»: пока каждая кнопка
 *  решала это сама, над раскладкой стояло до десяти штук в ряд.
 */
const TABS: OzonTab[] = ["content", "slots", "places", "onec", "change"];

export function OzonSupplyDetailPage(): React.ReactElement {
  const { shipmentId } = useParams();
  // возврат в реестр — на тот же срез (фильтры в адресе); прямой заход — на вкладку без фильтров
  const from = (useLocation().state as { from?: unknown } | null)?.from;
  const backTo = typeof from === "string" && from ? `/supply${from}` : "/supply?tab=ozon";
  const id = Number(shipmentId);
  const [asked, setTab] = useUrlParam("tab");
  // ⚠️ Режим — в АДРЕСЕ: страницу с идущей правкой надо уметь прислать коллеге и снять харнессом
  // скриншотов. Правдой он при этом НЕ является: идёт ли правка, знает только `d.change`
  const [mode, setMode] = useUrlParam("mode");
  const act = useOzonDirectionActions();

  const q = useQuery({
    queryKey: dirKey(id),
    queryFn: () => ozon.getOzonShipment(id),
    enabled: Number.isInteger(id),
  });
  // пункты сдачи и кванты короба — те же запросы, что у списка: ключи общие, и открытая рядом
  // вкладка списка отдаст их из кэша
  // ⚠️ Мониторинг мест: пока он активен, черновик, окна, заявку и пункт сдачи ведёт наблюдатель, а
  // бэкенд отвечает 409 на ручные кнопки — экран гасит их заранее и говорит почему
  const sw = useSlotWatch(id, Number.isInteger(id));
  const watching = sw.watch.data?.status === "active";
  // ⚠️ пока мониторинг ЧИТАЕТСЯ, активен он или нет, неизвестно — ручные действия заперты, а окна не
  // спрашиваем: бэкенд на них ответил бы 409. Упавшее чтение замком не считаем — оно видно ошибкой
  const locked = watching || sw.watch.isPending;
  // часы для «наблюдатель не отвечает»: опрос с тем же ответом страницу не перерисует
  const now = useNow(WATCH_POLL_MS, watching);
  const [watchOpen, setWatchOpen] = useState(false);
  const dropOffs = useQuery({
    queryKey: ["supply", "ozon", "drop-offs"],
    queryFn: ozon.getOzonDropOffs,
    staleTime: 60 * 60_000,
  });
  const runId = q.data?.run_id ?? null;
  const quants = useQuery({
    queryKey: ["supply", "ozon", "box-quants", runId],
    queryFn: () => ozon.getOzonBoxQuants(runId!),
    enabled: runId != null,
    staleTime: 30 * 60_000,
  });
  // ⚠️ раскладку спрашиваем только после заявки: до неё грузомест не существует (§4а плана), и
  // ручка ходила бы в площадку за лимитами впустую
  const layout = useOzonLayout(
    id,
    q.data?.supply_number ?? null,
    q.data?.state === "ordered",
  );
  // Правка состава: план каскада (по запросу — он ходит в 1С за ценами) и ЖИВОЙ шаг применения
  const change = useOzonChange(id, q.data?.change ?? null, {
    put: layout.put,
    afterLayoutChange: layout.afterLayoutChange,
    stamp: layout.stamp,
    preview: asked === "change",
  });
  // ⚠️ Раздел считаем ДО запроса окон: без `?tab=` его выбирает стадия (`defaultTab`), и у живого
  // черновика это как раз окна — спрашивай мы их по одному лишь адресу, раздел молчал бы пустым
  const stage = q.data ? stageOf(q.data) : null;
  const tab: OzonTab | "none" =
    asked === "none"
      ? "none"
      : TABS.includes(asked as OzonTab)
        ? (asked as OzonTab)
        : mode === "change"
          ? "places"
          : stage
            ? defaultTab(stage)
            : "none";
  // ключ включает черновик: пересняли — и окна прошлого черновика не подсунутся к новым числам
  const slotsQ = useQuery({
    queryKey: ["supply", "ozon", "timeslots", id, q.data?.draft_id],
    queryFn: () => ozon.getOzonTimeslots(id),
    // под мониторингом (и пока он читается) окна не спрашиваем: бэкенд ответит 409, окна ведёт наблюдатель
    enabled: tab === "slots" && q.data?.state === "drafted" && !locked,
    staleTime: 5 * 60_000,
  });

  if (!Number.isInteger(id))
    return (
      <Problem>
        В адресе вместо номера поставки «{shipmentId}» — открывать нечего.
      </Problem>
    );
  if (q.isError)
    return (
      <Problem>{extractErrorMessage(q.error, "Поставка не открылась")}</Problem>
    );
  const d = q.data;
  if (!d || stage === null)
    return <p className="p-4 text-sm text-muted-foreground">Загрузка…</p>;

  // ⚠️ пока НАПРАВЛЕНИЕ перезапрашивается (`afterLayoutChange` после каждой мутации), кнопки
  // гасим: стадия на экране ещё старая, и второй клик по «Отправить места» отправил бы их дважды
  const busy =
    act.busy(d.shipment_id) || layout.busy || change.busy || q.isFetching;
  // ⚠️ вместе с ошибками запросов раскладки и чек-листа: с ними пункт не исчезает, а называет
  // причину — иначе экран молчит ровно там, где собирался отправить места необратимо
  // ⚠️ Стадия, её фраза и кнопки считаются по ЖИВОМУ заходу: `d.change` перечитывается только
  // после того, как `apply` ответил (минуты), и по нему «Применить изменения» всё применение
  // оставалась бы нажимаемой, а «что дальше» звало бы применять уже применяемое
  const dir = change.change ? { ...d, change: change.change } : d;
  const stageActs = actionsFor(stage, dir, layout.info, layout.trouble, {
    preview: tab === "change",
    blocked: applyBlock(change.plan ?? null, change.planError),
  });
  const actions = locked ? lockForWatch(stageActs) : stageActs;
  // ⚠️ Напоминание о наклейках — про ПОСЛЕДНИЙ ЗАВЕРШЁННЫЙ заход, а ручка печати берёт
  // НОВЕЙШИЙ: при открытом новом заходе она ответит 409
  const reprintOff = reprintBlock(change.change);
  // ⚠️ Правки раскладки — только пока заход В ПРАВКЕ: с первого шага каскад идёт по сохранённому
  // плану, и переложенное место оставило бы его указывать на несуществующее
  const changeMode = change.change !== null && editing(change.change);

  /** Одно действие — один вызов. Подтверждения и тосты остались теми же, что были у кнопок. */
  const run = (key: ActionKey): void => {
    const docs = layout.docs;
    switch (key) {
      case "draft":
        act.draft(d.shipment_id);
        return;
      case "slots":
        setTab(tab === "slots" ? "none" : "slots");
        return;
      case "resolve":
        // ⚠️ не `q.refetch()`: ответа площадки в БД нет и не появится сам — состояние
        // направления меняет только сверка с кабинетом, а перечитывание показало бы то же
        // «заявка ушла, ответа нет» сколько угодно раз
        act.resolve(d.shipment_id);
        return;
      case "compute":
      case "recompute":
        // ⚠️ В режиме изменений пересчёт — ДРУГАЯ ручка и другая цена: раскладывается текущий
        // состав (а не согласованное площадкой, иначе правка молча отменилась бы), места с кодами
        // опустошаются, и каскад пойдёт заменой — коды сменятся у всех. Подтверждение обязано
        // назвать это поимённо, а не «ручные правки будут потеряны».
        if (changeMode) {
          if (
            window.confirm(
              [
                "Разложить заново? Раскладка будет пересобрана автоматом по текущему составу.",
                "Все коды грузомест будут ПЕРЕВЫПУЩЕНЫ, все этикетки придётся печатать заново.",
                "Ручные правки раскладки (переносы, сборка, разбор на короба) будут затёрты.",
              ].join("\n\n"),
            )
          ) {
            setTab("places");
            layout.edits.recompute.mutate();
          }
          return;
        }
        // ⚠️ пересчёт затирает раскладку целиком, вместе с ручными правками
        if (
          layout.places.length > 0 &&
          !window.confirm(
            "Пересчитать раскладку? Ручные правки будут потеряны.",
          )
        )
          return;
        setTab("places");
        layout.edits.compute.mutate();
        return;
      case "send": {
        const left = layout.sendable.length - layout.coded;
        if (
          window.confirm(
            `Отправить ${plural(left, "место", "места", "мест")} в поставку ${d.supply_number}? ` +
              "Площадка выдаст коды — по ним печатаются этикетки, и дальше состав правится через снятие кодов." +
              (layout.coded === 0
                ? " Места, стоящие в заявке сейчас, будут заменены нашей раскладкой."
                : ""),
          )
        )
          docs.send.mutate();
        return;
      }
      case "labels":
        docs.labels.mutate();
        return;
      case "pallets":
        docs.pallets.mutate();
        return;
      case "packing":
        docs.list.mutate();
        return;
      case "packing_xlsx":
        docs.xlsx.mutate();
        return;
      case "onec_run":
        if (
          window.confirm(
            "Записать поставку в 1С? Будут созданы заказ покупателя с резервом, паллеты, " +
              "ПТМО и счёт-фактура; передача и счёт-фактура ПРОВОДЯТСЯ. ПТМО фактически " +
              "бронирует товар на юрлице.",
          )
        )
          docs.onecRun.mutate();
        return;
      case "onec_reset":
        if (
          window.confirm(
            "Забыть документы 1С? Портал забудет заказ, паллеты, ПТМО и счёт-фактуру по " +
              "этой поставке и запишет её заново. Сами документы надо ДО этого пометить " +
              "на удаление в 1С — иначе портал откажет.",
          )
        )
          docs.reset.mutate();
        return;
      case "reissue":
        if (
          window.confirm(
            "Перевыпустить грузоместа? Партии подберутся заново, состав уедет на площадку " +
              "с заменой всего набора, и КОДЫ ПОМЕНЯЮТСЯ У ВСЕХ МЕСТ — напечатанные " +
              "этикетки станут недействительны, печатать придётся заново.",
          )
        )
          docs.reissue.mutate();
        return;
      case "cancel":
        // ⚠️ Отмена необратима в обе стороны: слот уходит другим продавцам (обратно тот же
        // могут и не дать), а коды грузомест умирают вместе с заявкой — напечатанные
        // этикетки после неё мусор.
        if (
          window.confirm(
            [
              `Отменить заявку ${d.supply_number ?? d.order_id ?? ""} (${d.region})?`,
              "Окно приёмки освободится — то же самое площадка может и не дать.",
              "Коды грузомест умрут вместе с заявкой: напечатанные этикетки станут недействительными.",
              d.onec
                ? `Документы 1С (заказ № ${d.onec.order_number}) отменой НЕ снимаются — их убирают в учёте руками.`
                : "",
              "Состав и раскладка останутся: после отмены можно сменить тип поставки или пункт сдачи и спросить площадку заново.",
            ]
              .filter(Boolean)
              .join("\n\n"),
          )
        )
          act.cancel(d.shipment_id);
        return;
      case "change":
        setTab("change");
        return;
      case "change_start":
        if (
          window.confirm(
            [
              "Открыть изменение состава? Само открытие обратимо: наружу ничего не уйдёт до кнопки «Применить изменения».",
              // ⚠️ Пока кодов нет, снимать и перепечатывать нечего — и говорить про это значило бы
              // пугать менеджера необратимостью, которой в этом случае просто не существует
              layout.coded > 0
                ? "Пока правка идёт, печать этикеток и запись в 1С заблокированы — бумаги покажут прежний состав."
                : "Кодов площадки у поставки ещё нет: снимать и перепечатывать нечего. После применения места уедут на площадку первой отправкой и получат коды.",
              "Увеличить СУММАРНОЕ количество заявки площадка не даёт: добавили одно — уберите столько же на другой позиции.",
            ].join("\n\n"),
          )
        ) {
          setMode("change");
          setTab("places");
          change.start.mutate();
        }
        return;
      case "change_abort":
        if (
          window.confirm(
            "Отменить изменения? Раскладка вернётся к тому, что уехало на площадку: места, коды, " +
              "коробы, партии и строки поставки — из снимка, снятого на старте правки. Всё, что " +
              "наменяли, будет потеряно.",
          )
        ) {
          setMode("");
          change.abort.mutate();
        }
        return;
      case "change_apply": {
        const plan = change.plan;
        // продолжение после сбоя плана не пересчитывает: каскад идёт по СОХРАНЁННОМУ
        const ask =
          change.change && editing(change.change)
            ? [
                "Применить изменения? Со второго шага это НЕОБРАТИМО.",
                plan && plan.places.emptied.length > 0
                  ? `Места №${plan.places.emptied.map((x) => x.position).join(", ")} будут сняты с площадки — напечатанные на них этикетки станут недействительными.`
                  : "",
                // ⚠️ Коды, которые слетят, обязаны быть НАЗВАНЫ и здесь: место остаётся жить, и
                // «изменится место №1» не говорит, какие наклейки станут мусором
                plan && plan.places.changed.length > 0
                  ? `У мест №${plan.places.changed
                      .map((x) => x.position)
                      .join(", ")} снимутся коды ${plan.places.changed.reduce(
                      (n, x) => n + x.cargo_ids.length,
                      0,
                    )} коробов (…${plan.places.changed
                      .flatMap((x) => x.cargo_ids)
                      .map((c) => c.slice(-6))
                      .join(
                        ", …",
                      )}) — их этикетки станут недействительными, наклейки остальных коробов ${
                      // ⚠️ та же оговорка, что в «Что изменится»: при смене набора палет их наклейки
                      // печатаются заново целиком, и обещать «палеты верны» здесь нельзя
                      plan.labels.pallets ? "верны, наклейки палет печатаются заново" : "и палет верны"
                    }.`
                  : "",
                plan && plan.places.new.length > 0
                  ? plan.labels.first_print
                    ? // кодов до правки не было: это ПЕРВАЯ отправка, и печатать заново нечего
                      `Мест на отправку: ${plan.places.new.length} — это первая отправка, коды им выдаст площадка, и этикетки печатаются уже после неё.`
                    : `Мест на досылку: ${plan.places.new.length} — коды им выдаст площадка, этикетки надо будет напечатать заново.`
                  : "",
                plan && plan.mode === "replace"
                  ? "РЕЖИМ ЗАМЕНЫ: нетронутых коробов с кодом не осталось, коды сменятся у ВСЕХ грузомест."
                  : "",
                plan?.onec
                  ? `Документы 1С (${plan.onec.docs.map((x) => x.number).join(", ")}) будут распроведены, переписаны на месте и проведены обратно — номера не меняются.`
                  : "",
              ]
                .filter(Boolean)
                .join("\n\n")
            : "Продолжить применение с того шага, на котором каскад сорвался? Что уже сделано, портал сверит у площадки.";
        if (window.confirm(ask)) change.apply.mutate();
        return;
      }
      case "labels_changed":
        layout.docs.labelsChanged.mutate();
        return;
    }
  };

  /** Крутить иконку у НАЖАТОГО действия, а не у всех сразу. */
  const pending = (key: ActionKey): boolean => {
    switch (key) {
      case "compute":
        return layout.edits.compute.isPending;
      case "recompute":
        // в режиме это отдельная ручка — крутить надо у той, которая правда летит
        return changeMode
          ? layout.edits.recompute.isPending
          : layout.edits.compute.isPending;
      case "send":
        return layout.docs.send.isPending;
      case "labels":
        return layout.docs.labels.isPending;
      case "pallets":
        return layout.docs.pallets.isPending;
      case "packing":
        return layout.docs.list.isPending;
      case "packing_xlsx":
        return layout.docs.xlsx.isPending;
      case "onec_run":
        return layout.docs.onecRun.isPending;
      case "onec_reset":
        return layout.docs.reset.isPending;
      case "reissue":
        return layout.docs.reissue.isPending;
      case "resolve":
        return act.busy(d.shipment_id);
      case "change_start":
        return change.start.isPending;
      case "change_abort":
        return change.abort.isPending;
      case "change_apply":
        return change.apply.isPending;
      case "labels_changed":
        return layout.docs.labelsChanged.isPending;
      case "draft":
      case "cancel":
        return act.busy(d.shipment_id);
      default:
        return false;
    }
  };

  const stale = draftStale(d);
  // взятое площадкой предложение — его объём (по её габаритам) и её же оговорка, если она есть
  const taken = takenOffer(d);
  const slotsError = slotsQ.isError
    ? extractErrorMessage(slotsQ.error, "Окна не пришли")
    : null;
  // раздел, которого на этой стадии ещё нет, — не пустое место, а объяснение: адрес можно
  // набрать руками, и молчащий экран выглядел бы поломкой
  const missing =
    tab === "places" && d.state !== "ordered"
      ? "Раскладка появится после заявки: грузоместа площадка принимает только в созданную поставку."
      : tab === "slots" && watching
        ? "Поставка на мониторинге мест: окно выберет и заявку создаст наблюдатель. Чтобы выбрать окно руками, снимите мониторинг."
        : tab === "slots" && d.state !== "drafted"
        ? "Окна приёмки даются только по живому черновику — снимите его кнопкой «Спросить площадку»."
        : tab === "content" && d.lines.length === 0
          ? "В этой поставке нет ни одной строки состава."
          : tab === "onec" && !d.onec
            ? "В 1С ещё ничего не записано: заказ, паллеты, ПТМО и счёт-фактура появятся здесь после кнопки «Записать в 1С»."
            : tab === "change" && !d.change
              ? "Изменение состава не начато: правка состава после заявки ведётся отдельной записью, и пока её нет, показывать нечего."
              : null;

  return (
    <div className="mx-auto max-w-none space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Link
          to={backTo}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> Поставки Ozon
        </Link>
        <h1 className="text-xl font-bold">{d.region}</h1>
        <span className="text-sm text-muted-foreground">{d.legal_name}</span>
        <StageBadge stage={stage} state={d.state} />
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="text-muted-foreground">
          просим <b className="text-foreground">{int0(d.requested)}</b>
          {d.state !== "" && (
            <>
              {" → возьмут "}
              <b
                className={cn(
                  d.dropped > 0
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-foreground",
                )}
              >
                {int0(d.accepted)}
              </b>
              {d.dropped > 0 && (
                <>
                  {" · отсекут "}
                  <b className="text-amber-600 dark:text-amber-400">
                    {int0(d.dropped)}
                  </b>
                </>
              )}
            </>
          )}
        </span>
        {taken?.volume_l != null && (
          <span
            className="text-xs text-muted-foreground"
            title="Объём взятого по габаритам карточек Ozon — по нему площадка и отказывает пункту за объём"
          >
            объём по Ozon:{" "}
            <b className="text-foreground">{fmt(taken.volume_l)} л</b>
          </span>
        )}
        {taken?.reason && (
          <span className="text-xs text-amber-600 dark:text-amber-400">
            {taken.reason}
          </span>
        )}
        {d.supply_number && (
          <span className="text-xs text-muted-foreground">
            поставка <b className="text-foreground">{d.supply_number}</b>
          </span>
        )}
        {d.slot_from && (
          <span className="text-xs text-muted-foreground">
            приёмка <b className="text-foreground">{fmtSlot(d.slot_from)}</b>
          </span>
        )}
        <KindPicker
          d={d}
          dropOffs={dropOffs.data ?? []}
          disabled={busy || locked}
          onPick={act.kind}
        />
        {dropOffs.error && (
          <span className="text-xs text-destructive">
            Список пунктов сдачи Ozon не загрузился:{" "}
            {extractErrorMessage(dropOffs.error, "ошибка")}. Пункт можно найти
            поиском по полному имени
          </span>
        )}
        <WarehousePicker
          d={d}
          disabled={busy || locked}
          onPick={(sid, warehouse) => act.warehouse({ id: sid, warehouse })}
        />
        {d.drafted_at && (
          <span
            className={cn(
              "text-xs",
              stale
                ? "text-amber-600 dark:text-amber-400"
                : "text-muted-foreground/70",
            )}
            title={fmtDateTime(d.drafted_at)}
          >
            черновик {timeAgo(d.drafted_at)}
            {stale && " — протух"}
          </span>
        )}
      </div>

      {/* ⚠️ Поломанный цикл 1С — КРАСНОЙ строкой: `onec` у такой поставки приходит пустым, и без
          неё экран молча говорил бы «в 1С ничего нет» про поставку, у которой заказ есть. */}
      {d.broken && (
        <div className="flex items-center gap-1.5 rounded border border-red-300 bg-red-50 px-3 py-1 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          <AlertTriangle className="size-3.5 shrink-0" /> {d.broken}
        </div>
      )}
      {d.note && (
        <div className="flex items-center gap-1.5 rounded border border-amber-300 bg-amber-50 px-3 py-1 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
          <AlertTriangle className="size-3.5 shrink-0" /> {d.note}
        </div>
      )}

      {/* ⚠️ Полоса режима — ВЫШЕ прогресса стадий: пока правка не применена, всё, что ниже
          (числа, бумаги, 1С), показывает ПРЕЖНЮЮ поставку */}
      {change.change && (
        <ChangeModeBar
          change={change.change}
          // ⚠️ Пока каскад идёт, счёт правок берём из СОХРАНЁННОГО плана: он и есть то, по чему
          // работают шаги, а пересчитанное превью в это время не считается вовсе. Сохранённый —
          // только через `savedPlan`: у планов до 18.09.2026 нет класса `changed`
          plan={change.plan ?? ozon.savedPlan(change.change)}
          busy={busy}
          onPreview={() => setTab("change")}
          onAbort={() => run("change_abort")}
        />
      )}

      {/* Правка кончилась, но короба новых мест стоят без наклеек — и об этом не напомнит никто,
          кроме экрана: стопку печатает человек */}
      {d.reprint && d.reprint.positions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
          <Printer className="size-4 shrink-0" />
          {/* ⚠️ «Новых коробов», а не «мест»: изменённое место правка тронула по коробам, и
              стопка тут ровно на те коробы, что завела досылка, — у уцелевших наклейки верны */}
          <span>
            Перепечатайте этикетки новых коробов мест №
            {d.reprint.positions.join(", ")} ({int0(d.reprint.codes)} кодов) —
            их завела применённая правка, у остальных наклейки верны.
          </span>
          <Button
            size="sm"
            className="ml-auto"
            disabled={
              busy || reprintOff !== null || layout.docs.labelsChanged.isPending
            }
            onClick={() => run("labels_changed")}
            title={
              reprintOff ??
              "Стопка только на НОВЫЕ коробы: полная отправила бы склад переклеивать сотни коробов"
            }
          >
            <Printer className="size-4" /> Этикетки затронутых (
            {int0(d.reprint.codes)})
          </Button>
          {/* ⚠️ Погашенная кнопка объясняется ТЕКСТОМ, а не одним `title`: иначе напоминание
              зовёт печатать, кнопка не нажимается, и почему — не сказано нигде */}
          {reprintOff && (
            <span className="w-full text-xs">
              Кнопка погашена — {reprintOff}: стопка печатается по НОВЕЙШЕМУ
              заходу, а эти места завёл прошлый.
            </span>
          )}
        </div>
      )}

      <div className="space-y-2 rounded-md border bg-card px-3 py-2">
        <StageProgress stage={stage} d={dir} />
        <div className="flex flex-wrap items-center gap-2">
          <StageActions
            actions={actions}
            busy={busy}
            pending={pending}
            active={
              tab === "slots" ? "slots" : tab === "change" ? "change" : null
            }
            onAction={run}
          />
          {/* ⚠️ только когда мониторинг ПРОЧИТАН: не зная, стоит ли поставка на нём, кнопка
              предлагала бы поставить второй */}
          {canWatch(d) && sw.watch.isSuccess && !watching && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              title="Наблюдатель раз в минуту смотрит окна пункта сдачи и сам создаёт заявку на первое подходящее"
              onClick={() => {
                sw.start.reset();
                setWatchOpen(true);
              }}
            >
              <Radar className="size-4" /> Мониторинг мест
            </Button>
          )}
        </div>
        {watching && (
          <p className="text-xs text-sky-700 dark:text-sky-400">
            Поставка на мониторинге мест: черновик, окна, заявку и пункт сдачи ведёт наблюдатель —
            ручные действия заперты. Снимите мониторинг, чтобы действовать руками.
          </p>
        )}
      </div>

      {sw.watch.isError && (
        <p className="text-xs text-destructive">
          ⚠ Мониторинг мест не прочитался:{" "}
          {extractErrorMessage(sw.watch.error, "ошибка")}
        </p>
      )}
      {sw.watch.data && (
        <SlotWatchCard
          w={sw.watch.data}
          now={now}
          stopping={sw.stop.isPending}
          stopError={
            sw.stop.isError
              ? extractErrorMessage(sw.stop.error, "Мониторинг не снят")
              : null
          }
          onAskStop={() => sw.stop.reset()}
          onStop={(done) => sw.stop.mutate(undefined, { onSuccess: done })}
        />
      )}
      {watchOpen && (
        <SlotWatchDialog
          d={d}
          dropOffs={dropOffs.data ?? []}
          dropOffsError={
            dropOffs.isError
              ? extractErrorMessage(dropOffs.error, "ошибка")
              : null
          }
          pending={sw.start.isPending}
          error={
            sw.start.isError
              ? extractErrorMessage(sw.start.error, "Мониторинг не поставлен")
              : null
          }
          onSubmit={(body) =>
            sw.start.mutate(body, { onSuccess: () => setWatchOpen(false) })
          }
          onClose={() => setWatchOpen(false)}
        />
      )}
      {/* ⚠️ над вкладками, а не на «1С»: каскад правки на конфликте закончился, и иначе о том,
          что склад собирает по старому плану, узнали бы только зайдя туда */}
      {d.onec?.pallet_plan_conflict && (
        <PalletPlanConflict conflict={d.onec.pallet_plan_conflict} />
      )}

      <div className="flex flex-wrap items-center gap-1 text-sm">
        <Tab tab="content" cur={tab} setTab={setTab} label="Состав" />
        {d.state === "ordered" && (
          <Tab tab="places" cur={tab} setTab={setTab} label="Раскладка" />
        )}
        {d.onec && <Tab tab="onec" cur={tab} setTab={setTab} label="1С" />}
        {d.change && (
          <Tab tab="change" cur={tab} setTab={setTab} label="Что изменится" />
        )}
      </div>

      {missing && <p className="text-sm text-muted-foreground">{missing}</p>}

      {tab === "content" && d.lines.length > 0 && watching && (
        <p className="text-xs text-sky-700 dark:text-sky-400">
          Правка состава недоступна: {WATCH_LOCK}.
        </p>
      )}
      {tab === "content" && d.lines.length > 0 && (
        <ContentPanel
          d={d}
          quants={quants.data ?? {}}
          quantsError={
            quants.isError
              ? extractErrorMessage(quants.error, "1С не ответила")
              : null
          }
          busy={busy || locked}
          onApply={(qty) => act.content({ id: d.shipment_id, qty })}
        />
      )}

      {tab === "slots" && slotsError && !locked && (
        <p className="text-xs text-red-600 dark:text-red-400">
          ⚠ {slotsError}
          {stale &&
            " — нажмите «Спросить заново»: окна даются только по живому черновику."}
        </p>
      )}
      {tab === "slots" && slotsQ.data && d.state === "drafted" && !locked && (
        <div className="rounded-md border bg-card">
          <SlotPicker
            slots={slotsQ.data}
            busy={busy}
            onPick={(slot) => act.order({ id: d.shipment_id, slot })}
          />
        </div>
      )}

      {tab === "places" && d.state === "ordered" && (
        <PlacesSection
          layout={layout}
          transport={d.transport}
          shipmentId={d.shipment_id}
          changeMode={changeMode}
        />
      )}

      {tab === "onec" && d.onec && (
        <div className="space-y-1 text-xs">
          <OnecBanner onec={d.onec} />
          {d.onec.pallets_target === "portal" && (
            <PalletPlanBanner shipmentId={d.shipment_id} />
          )}
          <p className="text-muted-foreground">
            Записать поставку в 1С, продолжить цикл или забыть документы — одной
            кнопкой стадии наверху и меню «…» рядом с ней.
          </p>
        </div>
      )}

      {/* ⚠️ Раздел — только пока правка ИДЁТ: без активной записи честный ответ другой
          («изменение не начато»), и он уже сказан выше */}
      {tab === "change" && change.change && (
        <ChangePreview
          change={change.change}
          plan={change.plan}
          error={change.planError}
          loading={change.planLoading}
          palletsToPortal={d.onec?.pallets_target === "portal"}
        />
      )}
    </div>
  );
}

/** Переключатель раздела. Раздел живёт в АДРЕСЕ: его надо уметь прислать коллеге и снять
 *  харнессом скриншотов. Повторное нажатие сворачивает всё (`?tab=none`). */
function Tab({
  tab,
  cur,
  setTab,
  label,
}: {
  tab: OzonTab;
  cur: OzonTab | "none";
  setTab: (v: string) => void;
  label: string;
}): React.ReactElement {
  return (
    <Button
      size="sm"
      variant="outline"
      className={cn(cur === tab && "bg-accent")}
      onClick={() => setTab(cur === tab ? "none" : tab)}
    >
      {label}
    </Button>
  );
}

function Problem({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="m-4 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
      ⚠ {children}
    </div>
  );
}
